import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const CACHE_KEYS = {
  MESSAGES: 'messages_cache_v1',
  CUSTOMERS: 'customers_cache_v1',
  CALL_HISTORY: 'call_history_cache_v1',
  INTEGRATION_STATUS: 'integration_status_cache_v1',
  CONVERSATION_SUMMARIES: 'conversation_summaries_cache_v1',
} as const;

const INTEGRATION_TTL_MS = 60 * 60 * 1000; // 1 hour

interface CachedData<T> {
  data: T;
  timestamp: number;
  businessId: string;
}

interface IntegrationStatus {
  hasTwilio: boolean;
  hasGmail: boolean;
}

const isNative = () => Capacitor.isNativePlatform();

async function setCache<T>(key: string, data: T, businessId: string): Promise<void> {
  try {
    if (!isNative()) return;
    const payload: CachedData<T> = { data, timestamp: Date.now(), businessId };
    await Preferences.set({ key, value: JSON.stringify(payload) });
  } catch (e) {
    console.warn('[MessagesCache] Failed to write cache:', key, e);
  }
}

async function getCache<T>(key: string, businessId: string): Promise<{ data: T; timestamp: number } | null> {
  try {
    if (!isNative()) return null;
    const { value } = await Preferences.get({ key });
    if (!value) return null;
    const parsed: CachedData<T> = JSON.parse(value);
    if (parsed.businessId !== businessId) return null;
    return { data: parsed.data, timestamp: parsed.timestamp };
  } catch (e) {
    console.warn('[MessagesCache] Failed to read cache:', key, e);
    return null;
  }
}

// ── Messages ──

export async function cacheMessages(messages: any[], businessId: string): Promise<void> {
  await setCache(CACHE_KEYS.MESSAGES, messages, businessId);
}

export async function getCachedMessages(businessId: string): Promise<any[] | null> {
  const result = await getCache<any[]>(CACHE_KEYS.MESSAGES, businessId);
  return result?.data ?? null;
}

// ── Customers ──

export async function cacheCustomers(customers: any[], businessId: string): Promise<void> {
  await setCache(CACHE_KEYS.CUSTOMERS, customers, businessId);
}

export async function getCachedCustomers(businessId: string): Promise<any[] | null> {
  const result = await getCache<any[]>(CACHE_KEYS.CUSTOMERS, businessId);
  return result?.data ?? null;
}

// ── Call History ──

export async function cacheCallHistory(calls: any[], businessId: string): Promise<void> {
  await setCache(CACHE_KEYS.CALL_HISTORY, calls, businessId);
}

export async function getCachedCallHistory(businessId: string): Promise<any[] | null> {
  const result = await getCache<any[]>(CACHE_KEYS.CALL_HISTORY, businessId);
  return result?.data ?? null;
}

// ── Conversation summaries (the inbox list — painted instantly on open) ──

export async function cacheConversationSummaries(cards: any[], businessId: string): Promise<void> {
  await setCache(CACHE_KEYS.CONVERSATION_SUMMARIES, cards, businessId);
}

export async function getCachedConversationSummaries(businessId: string): Promise<any[] | null> {
  const result = await getCache<any[]>(CACHE_KEYS.CONVERSATION_SUMMARIES, businessId);
  return result?.data ?? null;
}

// ── Integration Status (with TTL) ──

export async function cacheIntegrationStatus(status: IntegrationStatus, businessId: string): Promise<void> {
  await setCache(CACHE_KEYS.INTEGRATION_STATUS, status, businessId);
}

export async function getCachedIntegrationStatus(businessId: string): Promise<IntegrationStatus | null> {
  const result = await getCache<IntegrationStatus>(CACHE_KEYS.INTEGRATION_STATUS, businessId);
  if (!result) return null;
  if (Date.now() - result.timestamp > INTEGRATION_TTL_MS) return null;
  return result.data;
}

// ── Clear all caches (e.g. on logout) ──

export async function clearMessagesCache(): Promise<void> {
  try {
    if (!isNative()) return;
    await Promise.all(
      Object.values(CACHE_KEYS).map(key => Preferences.remove({ key }))
    );
  } catch (e) {
    console.warn('[MessagesCache] Failed to clear cache:', e);
  }
}
